import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Locations.css';
import './LocationsCards.css';

const LocationsContent = (props) => {
    const [locations, setLocations] = useState([]);
    const [userLocation, setUserLocation] = useState(null);
    const [distances, setDistances] = useState({});
    const [loading, setLoading] = useState(true);
    const [locationPermission, setLocationPermission] = useState('prompt');
    const [mapCenter, setMapCenter] = useState(null);
    const [mapLoaded, setMapLoaded] = useState(false);
    const [mapError, setMapError] = useState(null);
    
    const mapRef = useRef(null);
    const googleMapRef = useRef(null);
    const markersRef = useRef([]);
    const mapInitialized = useRef(false);

    // Google Maps API key from environment variable
    const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    // Fetch locations data - this runs immediately and independently
    useEffect(() => {
        fetchLocations();
        fetchMapCenter();
    }, []);

    // Load Google Maps API only after locations are loaded
    useEffect(() => {
        if (locations.length > 0 && mapCenter && GOOGLE_MAPS_API_KEY && !mapInitialized.current) {
            loadGoogleMapsWithTimeout();
        }
    }, [locations, mapCenter, GOOGLE_MAPS_API_KEY]);

    const fetchLocations = async () => {
        try {
            const response = await fetch('/api/locations');
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const result = await response.json();
            
            if (result.success) {
                setLocations(result.data);
            } else {
                console.error('Failed to load locations:', result.message);
            }
        } catch (err) {
            console.error('Error fetching locations:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchMapCenter = async () => {
        try {
            const response = await fetch('/api/locations/map/center');
            const result = await response.json();
            
            if (result.success) {
                setMapCenter(result.data.center);
            }
        } catch (err) {
            console.error('Error fetching map center:', err);
            setMapCenter({ latitude: 37.9571953, longitude: -122.3122546 });
        }
    };

    const loadGoogleMapsWithTimeout = () => {
        if (mapInitialized.current) return;
        mapInitialized.current = true;

        // Set a timeout for map loading - if it fails, continue without map
        const mapTimeout = setTimeout(() => {
            if (!mapLoaded) {
                console.warn('Map loading took too long, continuing without map');
                setMapError('Map loading timeout - locations still available');
            }
        }, 10000); // 10 second timeout

        // Check if already loaded
        if (window.google && window.google.maps && window.google.maps.Map) {
            clearTimeout(mapTimeout);
            setMapLoaded(true);
            setTimeout(initializeMap, 100);
            return;
        }

        // Load the script
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=geometry&v=weekly&callback=initGoogleMapsCallback`;
        script.async = true;

        window.initGoogleMapsCallback = () => {
            clearTimeout(mapTimeout);
            setMapLoaded(true);
            setTimeout(initializeMap, 100);
            delete window.initGoogleMapsCallback;
        };

        script.onerror = () => {
            clearTimeout(mapTimeout);
            setMapError('Failed to load Google Maps');
            delete window.initGoogleMapsCallback;
        };

        document.head.appendChild(script);
    };

    const initializeMap = () => {
        if (!window.google || !window.google.maps || !mapRef.current || !mapCenter) {
            return;
        }

        try {
            const map = new window.google.maps.Map(mapRef.current, {
                center: { lat: mapCenter.latitude, lng: mapCenter.longitude },
                zoom: 11,
                mapTypeControl: false,        // Hides Map/Satellite buttons
                streetViewControl: false,     // Hides Street View control
                fullscreenControl: false, // Optional
                styles: [
                    {
                        featureType: "poi",
                        elementType: "labels",
                        stylers: [{ visibility: "off" }]
                    }
                ]
            });

            googleMapRef.current = map;

            // Clear existing markers
            markersRef.current.forEach(marker => {
                if (marker && marker.setMap) {
                    marker.setMap(null);
                }
            });
            markersRef.current = [];

            // Add markers
            locations.forEach((location) => {
                try {
                    const marker = new window.google.maps.Marker({
                        position: { 
                            lat: location.coordinates.latitude, 
                            lng: location.coordinates.longitude 
                        },
                        map: map,
                        title: location.name,
                        icon: {
                            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(createMarkerSVG()),
                            scaledSize: new window.google.maps.Size(32, 40)
                        }
                    });

                    const infoWindow = new window.google.maps.InfoWindow({
                        content: createInfoWindowContent(location)
                    });

                    marker.addListener('click', () => {
                        infoWindow.open(map, marker);
                    });

                    markersRef.current.push(marker);
                } catch (markerError) {
                    console.error('Error creating marker:', markerError);
                }
            });

            // Fit bounds
            if (locations.length > 1) {
                try {
                    const bounds = new window.google.maps.LatLngBounds();
                    locations.forEach(location => {
                        bounds.extend({
                            lat: location.coordinates.latitude,
                            lng: location.coordinates.longitude
                        });
                    });
                    map.fitBounds(bounds);
                } catch (boundsError) {
                    console.error('Error fitting bounds:', boundsError);
                }
            }
        } catch (mapError) {
            console.error('Error initializing map:', mapError);
            setMapError('Map initialization failed');
        }
    };

    const requestUserLocation = () => {
        if (!navigator.geolocation) {
            setLocationPermission('denied');
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude } = position.coords;
                setUserLocation({ latitude, longitude });
                setLocationPermission('granted');
                calculateDistances(latitude, longitude);
            },
            (error) => {
                console.error('Geolocation error:', error);
                setLocationPermission('denied');
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 300000
            }
        );
    };

    const calculateDistances = async (latitude, longitude) => {
        try {
            const response = await fetch('/api/locations/distances', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ latitude, longitude })
            });
            
            const result = await response.json();
            
            if (result.success) {
                const distanceMap = {};
                result.data.forEach(location => {
                    distanceMap[location._id] = location.distance;
                });
                setDistances(distanceMap);
            }
        } catch (err) {
            console.error('Error calculating distances:', err);
        }
    };

    const createMarkerSVG = () => {
        return `
            <svg width="32" height="40" viewBox="0 0 32 40" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 0C7.2 0 0 7.2 0 16c0 8.8 16 24 16 24s16-15.2 16-24C32 7.2 24.8 0 16 0z" fill="#8B1E1E"/>
                <circle cx="16" cy="16" r="8" fill="white"/>
                <text x="16" y="20" text-anchor="middle" font-family="Arial" font-size="12" font-weight="bold" fill="#8B1E1E">K</text>
            </svg>
        `;
    };

    const createInfoWindowContent = (location) => {
        return `
            <div class="m-0 p-2 is-flex is-flex-direction-column is-justify-content-space-between">
                <h4 class="infoWindow mb-1">${location.name}</h4>
                <p class="mb-0 small">
                    <a href="${location.googleDirectionsUrl}" target="_blank">
                    ${location.address.address1}
                    ${location.address.address2}
                    </a>
                </p>
                <p class="mt-0 mb-1 small">
                    <a href="${location.googleDirectionsUrl}" target="_blank">
                    ${location.address.city}, ${location.address.state} ${location.address.zip}
                    </a>
                </p>
                <p class="mt-1 mb-1 small">
                   ${location.phoneNumber}
                </p>
                <p class="mt-2 tiny">
                    ${location.currentStatus.message}
                </p>
            </div>
        `;
    };

    const formatDistance = (distance) => {
        if (distance === undefined || distance === null) return '';
        return distance < 1 ? `${(distance * 5280).toFixed(0)} ft` : `${distance.toFixed(1)} mi`;
    };

    const LocationCard = ({ location }) => {
        const distance = distances[location._id];
        const showDistance = locationPermission === 'granted' && distance !== undefined;
        
        return (
            <div className="cardLocations mb-4" style={{ height: 'auto' }}>
                <div className="p-3">
                    {showDistance ? (
                        <div className="is-flex is-flex-direction-row is-justify-content-space-between">
                            <div className="column is-three-quarters">
                                <h3 className="locationCard mb-1">{location.name}</h3>
                                <p className="small">
                                    {location.address.address1}
                                    {location.address.address2 && `, ${location.address.address2}`}
                                </p>
                               <p className="mb-1 small">
                                    {location.address.city}, {location.address.state} {location.address.zip}
                                </p>
                                <p className="mb-1 small">
                                    {location.phoneNumber}
                                </p>
                                <p className={`mt-2 tiny ${
                                    location.currentStatus.isOpen ? 'colorBlack' : 'colorBlack'
                                }`}>
                                    {location.currentStatus.message}
                                </p>
                            </div>
                            <div className="mb-4 is-flex is-align-items-end">
                                <div className="is-flex is-flex-direction-column is-justify-content-space-between" style={{ height: '100%' }}>
                                    <div className='is-flex is-justify-content-end'>
                                        <p className="tiny colorYellow is-bold">
                                            {/* <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M7.66667 2.66667V11M7.66667 2.66667L11 1V9.33333L7.66667 11M7.66667 2.66667L4.33333 1M7.66667 11L4.33333 9.33333M4.33333 9.33333L1 11V2.66667L4.33333 1M4.33333 9.33333V1" stroke="#F2A341" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg> */}

                                            {formatDistance(distance)}
                                        </p>
                                    </div>
                                    <button 
                                        className="view-button"
                                        onClick={() => window.open(`/locations/${location.name.toLowerCase().replace(/\s+/g, '-')}`, '_blank')}
                                    >
                                        View
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="is-flex is-flex-direction-row is-justify-content-space-between">
                            <div className="column is-three-quarters">
                                <h3 className="locationCard mb-1">{location.name}</h3>
                                <p className="small">
                                    {location.address.address1}
                                    {location.address.address2 && `, ${location.address.address2}`}
                                </p>
                                <p className="mb-1 small">
                                    {location.address.city}, {location.address.state} {location.address.zip}
                                </p>
                                <p className="mb-1 small">
                                    {location.phoneNumber}
                                </p>
                                <p className={`mt-2 tiny ${
                                    location.currentStatus.isOpen ? 'colorBlack' : 'colorBlack'
                                }`}>
                                    {location.currentStatus.message}
                                </p>
                            </div>
                            <div className="mb-4 is-flex is-align-items-end">
                                <button 
                                    className="view-button"
                                    onClick={() => window.open(`${location.websiteUrl}`, '_self')}
                                >
                                    View
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Cleanup effect
    useEffect(() => {
        return () => {
            markersRef.current.forEach(marker => {
                if (marker && marker.setMap) {
                    marker.setMap(null);
                }
            });
            markersRef.current = [];
            
            if (googleMapRef.current) {
                googleMapRef.current = null;
            }
            
            if (window.initGoogleMapsCallback) {
                delete window.initGoogleMapsCallback;
            }
        };
    }, []);

    // Always show the layout, even if still loading
    return (
        <div className={`column is-flex is-flex-direction-column ${props.className}`}>
            <div className="is-flex is-flex-direction-row is-justify-content-space-between is-flex-wrap-wrap">
                <div>
                    <h1 className="h1 colorBlack">{props.title}</h1>
                </div>
                <div className='is-flex is-align-items-end'>
                    {locationPermission === 'prompt' && (
                        <div className="p-0 mb-4">
                            <p className="tiny">
                                For the distance to each store, <Link onClick={requestUserLocation}> enable your location.</Link>
                            </p>
                        </div>
                    )}
                    
                    {locationPermission === 'denied' && (
                        <div className="notification is-warning is-light mb-4">
                            <p className="small">
                                <strong>Distance feature unavailable.</strong> Enable location in your browser settings to see distances to stores.
                                <button 
                                    className="view-button"
                                    onClick={requestUserLocation}
                                >
                                    Try Again
                                </button>
                            </p>
                        </div>
                    )}
                </div>
            </div>
            <div className="columns is-variable is-2">
                {/* Left column - Location list */}
                <div className="column is-one-third pr-4">
                    <div className="location-list-container">
                        <div className="location-cards">
                            {loading ? (
                                <div className="py-4">
                                    <p>Loading locations...</p>
                                </div>
                            ) : locations.length > 0 ? (
                                locations.map(location => (
                                    <LocationCard key={location._id} location={location} />
                                ))
                            ) : (
                                <div className="py-4">
                                    <p>No locations found</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                
                {/* Right column - Google Map */}
                <div className="column is-two-thirds">
                    <div className="map-container-responsive">
                        {!GOOGLE_MAPS_API_KEY ? (
                            <div className="map-placeholder"></div>
                        ) : mapError ? (
                            <div className="map-placeholder">
                                <div className="">
                                    <p className='has-text-bold'>Map Issue {mapError}</p>
                                    <p className="mt-2">Locations are still available in the left panel.</p>
                                </div>
                            </div>
                        ) : !mapLoaded ? (
                            <div className="map-placeholder">
                                <div>
                                    <p>Loading map...</p>
                                </div>
                            </div>
                        ) : (
                            <div 
                                ref={mapRef} 
                                className="google-map-container"
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LocationsContent;